import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FilePlus, Edit, Trash2, Search, Loader2, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ItineraryList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  
  // Selection and Pagination
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemsPerPage = 10;

  const load = () => {
    setLoading(true);
    api.listItineraries().then((d) => setItems(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!confirm("Delete this itinerary?")) return;
    try {
      await api.deleteItinerary(id);
      toast.success("Itinerary deleted");
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [q]);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} itinerary(s)?`)) return;
    setIsDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => api.deleteItinerary(id)));
      toast.success(`${selectedIds.length} itinerary(s) deleted`);
      setItems(prev => prev.filter(i => !selectedIds.includes(i.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete some itineraries');
    } finally {
      setIsDeleting(false);
    }
  };

  const filtered = items.filter((i) =>
    `${i.customer_name} ${i.destination}`.toLowerCase().includes(q.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedItems.length && paginatedItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedItems.map(h => h.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(x => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-800">Itineraries</h1>
          <p className="text-slate-500">All saved client itineraries.</p>
        </div>
        <Link to="/admin/itineraries/new"><Button className="rounded-xl btn-luxe  w-full sm:w-auto" data-testid="list-new-itinerary-button"><FilePlus className="w-4 h-4 mr-2" /> New</Button></Link>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer or destination…" className="pl-9 rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="list-search-input" />
      </div>

      {selectedIds.length > 0 && (
        <div className="btn-luxe/20 border border-[#1D4ED8]/30 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-blue-200 font-medium ml-2">{selectedIds.length} itinerary(s) selected</span>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={isDeleting} className="rounded-lg">
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete Selected
          </Button>
        </div>
      )}

      <Card className="rounded-2xl glass-panel-dark border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-800" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-500" />
            <div className="mt-3 font-display font-semibold text-slate-800">No itineraries yet</div>
            <p className="text-sm text-slate-500">Create your first AI-generated itinerary.</p>
            <Link to="/admin/itineraries/new"><Button className="mt-4 rounded-xl btn-luxe ">Create Itinerary</Button></Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-4 py-3 w-12">
                    <Checkbox 
                      checked={paginatedItems.length > 0 && selectedIds.length === paginatedItems.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Travel</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((i) => (
                  <tr key={i.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Checkbox 
                        checked={selectedIds.includes(i.id)}
                        onCheckedChange={() => toggleSelect(i.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{i.customer_name}</div>
                      <div className="text-xs text-slate-500">{i.customer_contact}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{i.destination}</td>
                    <td className="px-4 py-3 text-slate-600">{i.days}D/{i.nights}N</td>
                    <td className="px-4 py-3 text-slate-600">{i.travel_month || "—"}</td>
                    <td className="px-4 py-3"><Badge className={i.status === "confirmed" ? "bg-green-100 text-green-700 hover:bg-green-200 border border-green-200" : "bg-slate-100 text-slate-700 border-slate-200"}>{i.status}</Badge></td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-teal-50 hover:text-teal-600" onClick={() => nav(`/admin/itineraries/${i.id}`)} data-testid={`list-edit-${i.id}`}><Edit className="w-4 h-4" /></Button>
                      <a href={`/print/${i.id}`} target="_blank" rel="noopener"><Button size="sm" variant="ghost" className="text-slate-600 hover:bg-blue-50 hover:text-blue-600"><FileText className="w-4 h-4" /></Button></a>
                      <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-red-50 hover:text-red-600" onClick={() => del(i.id)} data-testid={`list-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 mb-4 px-6">
                <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-slate-200 text-slate-800 hover:bg-slate-100">Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-slate-200 text-slate-800 hover:bg-slate-100">Next</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
